import React from 'react';
import { Table, Badge, Card, CardBody, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FaBuilding, FaEnvelope, FaPhone, FaMapMarkerAlt, FaUserTie } from 'react-icons/fa';
import './DashboardStyles.css';

interface Club {
  _id: string;
  name: string;
  logo: string;
  emailId: string;
  managerName: string;
  countryCode: number;
  managerContactNum: string | number;
  location: string;
}

interface RecentClubsTableProps {
  clubs: Club[];
  loading?: boolean;
}

const RecentClubsTable: React.FC<RecentClubsTableProps> = ({ clubs, loading = false }) => {
  const navigate = useNavigate();

  const formatContactNumber = (contact: string | number, countryCode: number = 91) => {
    if (!contact) return 'N/A';
    const contactStr = contact.toString();
    if (contactStr.length >= 10) {
      return `+${countryCode} ${contactStr}`;
    }
    return 'N/A';
  };

  const handleClubClick = (clubId: string) => {
    navigate(`/clubs/${clubId}`);
  };

  const handleViewAllClubs = () => {
    navigate('/all-clubs');
  };

  if (loading) {
    return (
      <Card className="h-100">
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Recent Clubs</h5>
          </div>
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </CardBody>
      </Card>
    );
  }

  if (!clubs || clubs.length === 0) {
    return (
      <Card className="h-100">
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Recent Clubs</h5>
          </div>
          <div className="text-center py-4">
            <p className="text-muted mb-0">No recent clubs found</p>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="h-100">
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Recent Clubs</h5>
          <Button 
            variant="link" 
            className="text-decoration-none p-0"
            onClick={handleViewAllClubs}
          >
            View all <i className="ph-arrow-right ms-1"></i>
          </Button>
        </div>
        
        <div className="table-responsive global-table-responsive">
          <Table className="global-table">
            <thead>
              <tr>
                <th scope="col">Club Name</th>
                <th scope="col">Manager</th>
                <th scope="col">Email</th>
                <th scope="col">Contact</th>
                <th scope="col">Location</th>
              </tr>
            </thead>
            <tbody>
              {clubs.length > 0 ? (
                clubs.slice(0, 5).map((club) => (
                  <tr key={club._id}>
                    <td>
                      <div className="d-flex align-items-center">
                        <div className="avatar-sm me-3">
                          {club.logo ? (
                            <img
                              src={club.logo}
                              alt={club.name}
                              className="avatar-title rounded-circle"
                              style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="avatar-title bg-primary-subtle rounded-circle">
                              <i className="ph-building text-primary"></i>
                            </div>
                          )}
                        </div>
                        <div>
                          <h6 className="mb-0">{club.name}</h6>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="text-body-secondary">
                        {club.managerName}
                      </span>
                    </td>
                    <td>
                      <span className="text-body-secondary">
                        {club.emailId}
                      </span>
                    </td>
                    <td>
                      <span className="text-body-secondary">
                        {formatContactNumber(club.managerContactNum, club.countryCode)}
                      </span>
                    </td>
                    <td>
                      <span className="text-body-secondary">
                        {club.location || 'N/A'}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="text-center py-4">
                    <i className="ph-building text-muted" style={{ fontSize: '2rem' }}></i>
                    <p className="text-muted mt-2 mb-0">No recent clubs</p>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </CardBody>
    </Card>
  );
};

export default RecentClubsTable; 